import useLatest from "@-/com.npmjs.use-latest";
import type { DeadlineExceededError } from "@-/dev.improve.haniverse.lib.deadline";
import { useEffect, useState } from "react";
import { armReadinessDeadline } from "#arm-readiness-deadline";

/**
 * The React form of {@link armReadinessDeadline}: while `waiting` is true, a deadline runs; if it
 * passes before `waiting` turns false, `onTimeout` runs once and the hook returns `true` so the
 * component can render its visible fallback (an error, a retry, a rebuilt surface).
 *
 * `attempt` names one wait: changing it (an operation id, a retry counter) re-arms a fresh
 * deadline and clears a previous expiry, so a rebuild gets its own full bound instead of
 * inheriting an already-expired one. `onTimeout` is read through a ref, so a fresh closure each
 * render neither restarts the deadline nor goes stale.
 */
export function useReadinessDeadline({
  attempt = null,
  deadlineMs,
  onTimeout,
  operation,
  waiting,
}: {
  attempt?: number | string | null;
  deadlineMs: number;
  onTimeout?: (error: DeadlineExceededError) => void;
  operation: string;
  waiting: boolean;
}): boolean {
  const onTimeoutRef = useLatest(onTimeout);
  const [expiredAttempt, setExpiredAttempt] = useState<{
    attempt: number | string | null;
  } | null>(null);

  useEffect(() => {
    if (!waiting) {
      return;
    }
    const disarm = armReadinessDeadline({
      deadlineMs,
      onTimeout: (error) => {
        setExpiredAttempt({ attempt });
        onTimeoutRef.current?.(error);
      },
      operation,
    });
    return () => {
      disarm();
      // The wait is over (reported, abandoned or replaced): a later wait starts un-expired.
      setExpiredAttempt(null);
    };
  }, [attempt, deadlineMs, onTimeoutRef, operation, waiting]);

  // Derived rather than reset in an effect: a finished wait or a new attempt is never "timed out",
  // not even for the one render before an effect could clear it.
  return waiting && expiredAttempt !== null && expiredAttempt.attempt === attempt;
}
